import React, { useState } from "react";
import ImagePreviewLoop from "../../common/imagepreviewloop";

const parseJSON = (data) => {
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const formatDate = (date) => {
  return new Date(date).toLocaleString();
};

const AttachmentPreview = ({ files, handleImageOpen }) => {
  if (!files || files.length === 0) return null;

  return (
    <div className="flex gap-2 mt-2 flex-wrap">
      {files.map((file, i) => {
        const url = file.url || file.path;
        const ext = (file.fileExt || "").toLowerCase();

        if (
          ext.includes("png") ||
          ext.includes("jpg") ||
          ext.includes("jpeg") ||
          ext.includes("gif")
        ) {
          return (
            <img
              key={i}
              src={url}
              alt="attachment"
              onClick={() => {
                handleImageOpen(url, i, files);
              }}
              className="w-20 h-20 object-cover rounded cursor-pointer"
            />
          );
        }

        if (ext.includes("mp4") || ext.includes("webm")) {
          return (
            <video key={i} onClick={() => {
                handleImageOpen(url, i, files);
              }} src={url} controls className="w-32 h-20 rounded cursor-pointer" />
          );
        }

        return (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline text-sm"
          >
            View File
          </a>
        );
      })}
    </div>
  );
};

const HistoryBlock = ({ histories = [], title, handleImageOpen }) => {
  if (!Array.isArray(histories) || histories.length === 0) return null;

  return (
    <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
      <p className="text-sm font-semibold text-red-600 mb-2">{title}</p>

      <div className="space-y-2">
        {histories.map((item, i) => (
          <div
            key={i}
            className="bg-white border rounded p-2 text-xs text-gray-700"
          >
            {Object.entries(item).map(([key, value]) => {
              if (!value) return null;

              // Handle arrays (like attachments inside history)
              if (Array.isArray(value)) {
                return (
                  <div key={key} className="mt-1">
                    <p className="font-medium capitalize">{key}:</p>
                    <AttachmentPreview
                      files={value}
                      handleImageOpen={handleImageOpen}
                    />
                  </div>
                );
              }

              // Handle dates
              if (key.toLowerCase().includes("date")) {
                return (
                  <p key={key}>
                    <span className="font-medium capitalize">{key}:</span>{" "}
                    {formatDate(value)}
                  </p>
                );
              }

              return (
                <p key={key}>
                  <span className="font-medium capitalize">{key}:</span>{" "}
                  {value.toString()}
                </p>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const TimelineCard = ({ item, handleImageOpen }) => {
  const values = parseJSON(item.timelineValues);

  const mainAttachments = values.attachment || [];

  return (
    <div className={`bg-white shadow-sm rounded-xl p-4 border`}>
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-800">{item.title}</h3>
        <span className="text-sm text-black font-bold">
          {formatDate(item.createdAt)}
        </span>
      </div>

      <div className="mt-2 text-sm text-gray-600 space-y-1 p-4">
        {Object.entries(values).map(([key, value]) => {
          if (Array.isArray(value)) return null;
          return (
            <p key={key}>
              <span className="font-medium capitalize">{key}:</span>{" "}
              {value?.toString()}
            </p>
          );
        })}
      </div>

      <AttachmentPreview
        files={mainAttachments}
        handleImageOpen={handleImageOpen}
      />

      {values.startingOdometerImage && (
        <AttachmentPreview
          files={values.startingOdometerImage}
          handleImageOpen={handleImageOpen}
        />
      )}
      {values.endOdometerImage && (
        <AttachmentPreview
          files={values.endOdometerImage}
          handleImageOpen={handleImageOpen}
        />
      )}

      <HistoryBlock
        histories={values.serviceEstimationHistories}
        title="Estimation History"
        handleImageOpen={handleImageOpen}
      />

      <HistoryBlock
        histories={values.paymentRequestRequestHistories}
        title="Payment Request History"
        handleImageOpen={handleImageOpen}
      />
    </div>
  );
};

export default function ServiceTimeline({ data }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);
  const [openImage, setOpenImage] = useState(false);
  const [imageSelection, setImageSelection] = useState(null);
  const rows = data || [];

  const sorted = [...rows].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  const getImageUrl = (item) => {
    if (!item) return null;
    if (typeof item === "string") return item;
    return item?.url || item?.fileUrl || item?.path || null;
  };

  const handleImageOpen = (img, idx = 0, imagesArray = []) => {
    setImageSelection(getImageUrl(img));
    setCurrentIndex(idx);
    setCurrentImages(imagesArray);
    setOpenImage(true);
  };

  return (
    <>
      {openImage && (
        <ImagePreviewLoop
          imageSelection={imageSelection}
          openImage={openImage}
          setImageSelection={setImageSelection}
          setOpenImage={setOpenImage}
          setCurrentIndex={setCurrentIndex}
          setCurrentImages={setCurrentImages}
          currentIndex={currentIndex}
          currentImages={currentImages}
        />
      )}
      <div className="p-4 space-y-4">
        {/* <h2 className="text-xl font-bold">Service Timeline Summary</h2> */}

        {sorted.map((item) => (
          <TimelineCard
            key={item.id}
            item={item}
            handleImageOpen={handleImageOpen}
          />
        ))}

        {sorted.length === 0 && (
          <p className="text-gray-500 text-sm">No timeline data found</p>
        )}
      </div>
    </>
  );
}
